import React from 'react';
import { X, Phone, Share2, CheckCircle2, Gift, Sparkles, LogIn, ChevronRight, Coins } from 'lucide-react';

interface GamificationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const GamificationModal: React.FC<GamificationModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const missions = [
        {
            id: 'phone',
            title: 'Xác thực số điện thoại',
            description: 'Tăng cường bảo mật và mở khóa đầy đủ tính năng.',
            reward: 100000,
            icon: Phone,
            color: 'text-blue-500',
            bg: 'bg-blue-50',
            status: 'pending',
            actionText: 'Xác thực ngay'
        },
        {
            id: 'social',
            title: 'Chia sẻ Trans BCTN',
            description: 'Mời bạn bè sử dụng để nhận credit không giới hạn.',
            reward: 5000,
            icon: Share2,
            color: 'text-fuchsia-500',
            bg: 'bg-fuchsia-50',
            status: 'pending',
            actionText: 'Lấy link chia sẻ'
        },
        {
            id: 'google',
            title: 'Liên kết Google',
            description: 'Đăng nhập nhanh chóng và đồng bộ hóa an toàn.',
            reward: 2000,
            icon: LogIn,
            color: 'text-amber-500',
            bg: 'bg-amber-50',
            status: 'completed',
            actionText: 'Đã hoàn thành'
        }
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header Profile/Banner */}
                <div className="relative bg-gradient-to-br from-violet-600 to-fuchsia-600 px-6 pt-8 pb-10 text-white overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="absolute top-0 right-0 p-4 z-10">
                        <button onClick={onClose} className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-md">
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                    
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 border border-white/30 shadow-inner backdrop-blur-sm relative">
                            <Gift className="w-8 h-8 text-white" />
                            <div className="absolute -top-2 -right-2 bg-amber-400 text-amber-950 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md animate-bounce">
                                HOT
                            </div>
                        </div>
                        <h2 className="text-xl font-black mb-1 flex items-center gap-2">
                            Trung Tâm Phần Thưởng <Sparkles className="w-5 h-5 text-amber-300" />
                        </h2>
                        <p className="text-sm font-medium text-white/80">Hoàn thành nhiệm vụ để nhận credit miễn phí</p>
                    </div>
                </div>

                {/* Content Body */}
                <div className="px-6 pb-6 pt-2 relative">
                    {/* Floating Info Card */}
                    <div className="bg-white rounded-2xl p-4 shadow-xl shadow-slate-200/50 -mt-8 relative z-20 border border-slate-100 flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-0.5">Số dư Credit của bạn</div>
                            <div className="text-2xl font-black text-slate-800 flex items-center gap-2">
                                42,500 <Coins className="w-5 h-5 text-emerald-500" />
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-bold text-slate-500">Đã nhận</div>
                            <div className="text-sm font-black text-emerald-600">+2,000</div>
                        </div>
                    </div>

                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Danh sách nhiệm vụ</h3>
                    
                    <div className="space-y-3">
                        {missions.map((mission) => (
                            <div 
                                key={mission.id} 
                                className={`flex items-center gap-4 p-3.5 rounded-2xl border transition-all ${
                                    mission.status === 'completed' 
                                        ? 'bg-slate-50 border-slate-100 opacity-70' 
                                        : 'bg-white border-slate-200 hover:border-violet-300 hover:shadow-md cursor-pointer group'
                                }`}
                            >
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${mission.bg} ${mission.color}`}>
                                    <mission.icon className="w-6 h-6" />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <h4 className={`font-bold truncate ${mission.status === 'completed' ? 'text-slate-500' : 'text-slate-800'}`}>
                                            {mission.title}
                                        </h4>
                                        {mission.status === 'completed' ? (
                                            <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                                <CheckCircle2 className="w-3 h-3" /> Xong
                                            </div>
                                        ) : (
                                            <div className="text-[11px] font-black text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
                                                +{mission.reward.toLocaleString()}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-[11px] font-medium text-slate-500 leading-snug line-clamp-2">
                                        {mission.description}
                                    </p>
                                </div>

                                {mission.status !== 'completed' && (
                                    <div className="shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors">
                                        <ChevronRight className="w-5 h-5" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
                    <p className="text-[10px] font-medium text-slate-400">
                        Nhiệm vụ mới sẽ được cập nhật hàng tuần. Các giới hạn và điều khoản được áp dụng.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default GamificationModal;
